import {
  DataRoomStatus,
  SpaceKind,
  viewingTimeParts,
  ViewingTimeUnit,
  WorkspaceMetrics,
} from 'shared';

import { StringKey } from '../../../lang/index';
import { getTranslation } from '../../../utils/get-translation.util';
import {
  HomeSpace,
  RecipientRoomView,
  SelectedSpace,
  WorkspaceCardStat,
  WorkspaceCardStatus,
} from '../types/data-room.types';
import { spaceSizeOf } from './space-format.util';

// The card sets its unit in its own type and reads it from the catalogue, where the tables beside
// it spell the word; the ladder that chooses the unit is the same one.
const VIEWING_TIME_UNIT_KEY: Record<ViewingTimeUnit, StringKey> = {
  [ViewingTimeUnit.HOURS]: 'HOURS',
  [ViewingTimeUnit.MINUTES]: 'MINS',
  [ViewingTimeUnit.SECONDS]: 'SECS',
};

export const isVaultSpace = ({ kind }: Pick<SelectedSpace, 'kind'>): boolean =>
  kind === SpaceKind.VAULT;

export const spaceCardStatus = ({
  kind,
  status,
}: Pick<SelectedSpace, 'kind' | 'status'>): undefined | WorkspaceCardStatus => {
  if (kind === SpaceKind.VAULT) {
    return WorkspaceCardStatus.LOCKED;
  }

  if (kind !== SpaceKind.DATA_ROOM || !status) {
    return undefined;
  }

  return status === DataRoomStatus.ACTIVE
    ? WorkspaceCardStatus.ACTIVE
    : WorkspaceCardStatus.INACTIVE;
};

export const toSelectedSpace = (space: HomeSpace): SelectedSpace => ({
  createdAt: space.createdAt,
  id: space.id,
  isLocked: space.kind === SpaceKind.VAULT,
  kind: space.kind,
  name: space.name,
  status: space.kind === SpaceKind.DATA_ROOM ? space.status : null,
});

const workspaceCardStats = (metrics: WorkspaceMetrics): WorkspaceCardStat[] => [
  { label: 'FILES', value: metrics.fileCount },
  { label: 'FOLDERS', value: metrics.folderCount },
  { label: 'CONTRIBUTOR', value: metrics.contributorCount },
  { label: 'SIZE', ...spaceSizeOf(metrics.totalSize) },
];

export const spaceCardStats = (space: HomeSpace): WorkspaceCardStat[] => {
  if (space.kind !== SpaceKind.DATA_ROOM) {
    return workspaceCardStats(space.metrics);
  }

  const viewingTime = viewingTimeParts(space.metrics.viewingTimeSeconds * 1000);

  return [
    { label: 'FILES', value: space.metrics.fileCount },
    { label: 'VIEWS', value: space.metrics.viewCount },
    { label: 'DOWNLOADS', value: space.metrics.downloadCount },
    {
      label: 'VIEWING_TIME',
      unit: VIEWING_TIME_UNIT_KEY[viewingTime.unit],
      value: viewingTime.value,
    },
  ];
};

/**
 * What a recipient's card counts: the contents of the room, in the order the design shows them.
 *
 * Not the owner's four — views, downloads and time spent are how the room's own people read their
 * visitors, and the route that answers a recipient does not carry them.
 */
export const recipientRoomStats = ({
  fileCount,
  folderCount,
  totalSize,
}: RecipientRoomView['metrics']): WorkspaceCardStat[] => [
  { label: 'FOLDERS', value: folderCount },
  { label: 'FILES', value: fileCount },
  { label: 'SIZE', ...spaceSizeOf(totalSize) },
];

// Null only for a Vault that holds nothing yet — it has no date to show.
export const spaceLastUpdatedIso = (space: HomeSpace): null | string =>
  space.metrics.lastActivityAt ?? space.updatedAt;

// The Vault has no description of its own; it says what it is for.
export const spaceDescription = (space: HomeSpace): string =>
  space.kind === SpaceKind.VAULT
    ? getTranslation('VAULT_WORKSPACE_DESCRIPTION')
    : (space.description ?? '');
